import React, { useEffect, useState } from 'react'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import {
  PopoverMenu,
  MenuTrigger,
  MenuItem,
  Menu
} from '@contrast-security-inc/design-system-popovers'
import { ShowXmlAsyncDownloadToast } from './show-xml-async-download-toast'
import {
  IconDownload,
  IconSize
} from '@contrast-security-inc/design-system-foundations'
import { useIntl } from 'react-intl'
import { ShowCsvAsyncDownloadToast } from './show-csv-async-download-toast'

export const actionTestID = 'vuln-export-test-id'

interface Props {
  organizationID: string
  excludedVulnerabilities: string[]
  filters: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
  isFreemiumOrganization: boolean
  isAppsGrid?: boolean
  size?: IconSize
}

export function ExportVulnerabilitiesWithFilterAction({
  filters,
  additionalFilters,
  organizationID,
  excludedVulnerabilities,
  isFreemiumOrganization,
  size,
  isAppsGrid = false
}: Props) {
  const { filterText, quickFilter, aggregations } = filters

  const [doXmlDownload, setDoXmlDownload] = useState(false)
  const [doCsvDownload, setDoCsvDownload] = useState(false)

  const additionalFiltersInputs: Input[] = []

  const { formatMessage } = useIntl()
  const iconSize: IconSize = size as IconSize

  useEffect(() => {
    if (doXmlDownload) {
      setDoXmlDownload(false)
    }

    if (doCsvDownload) {
      setDoCsvDownload(false)
    }
  }, [doXmlDownload, doCsvDownload])

  /* TODO verify if there is a better way to pass the additional filters, currently we are only processing the below listed: timestampFilter, startDate, endDate, metadataFilters */
  if (!!additionalFilters) {
    if (additionalFilters.dateFilter) {
      additionalFiltersInputs.push({
        name: 'timestampFilter',
        value: additionalFilters.dateFilter.field
      })
      additionalFiltersInputs.push({
        name: 'startDate',
        value:
          additionalFilters.dateFilter &&
          new Date(additionalFilters.dateFilter.start).getTime().toString()
      })
      additionalFiltersInputs.push({
        name: 'endDate',
        value:
          additionalFilters.dateFilter &&
          new Date(additionalFilters.dateFilter.end).getTime().toString()
      })
    }
    if (additionalFilters.sessionMetadata) {
      additionalFiltersInputs.push({
        name: 'metadataFilters',
        value: JSON.stringify(additionalFilters.sessionMetadata)
      })
    }

    if (additionalFilters.applicationMetadata) {
      additionalFiltersInputs.push({
        name: 'applicationMetadataFilters',
        value: additionalFilters.applicationMetadata
      })
    }
  }

  const asyncOptions = Object.assign(
    {
      filterText,
      quickFilter,
      excludedTraces: excludedVulnerabilities
    },
    filtersToAsyncOptions(aggregations, isAppsGrid),
    additionalFiltersInputs.reduce((acc, input) => {
      acc[input.name] =
        input.name === 'metadataFilters' ? JSON.parse(input.value) : input.value
      return acc
    }, {})
  )

  return (
    <>
      <PopoverMenu testId="popover-menu">
        <Menu>
          <MenuItem itemID="csv" onClick={() => setDoCsvDownload(true)}>
            CSV
          </MenuItem>
          {!isFreemiumOrganization ? (
            <MenuItem itemID="xml" onClick={() => setDoXmlDownload(true)}>
              XML
            </MenuItem>
          ) : (
            <></>
          )}
        </Menu>
        <MenuTrigger>
          <ActionButton
            data-testid={actionTestID}
            testId={actionTestID}
            IconButtonProps={{
              Icon: IconDownload,
              'aria-label': formatMessage({ id: 'action_export_data' }),
              size: iconSize
            }}
            TooltipProps={{
              placement: 'top',
              body: formatMessage({ id: 'action_export_data' })
            }}
          />
        </MenuTrigger>
      </PopoverMenu>
      {doXmlDownload && (
        <ShowXmlAsyncDownloadToast
          organizationID={organizationID}
          xmlOptions={asyncOptions}
        />
      )}
      {doCsvDownload && (
        <ShowCsvAsyncDownloadToast
          organizationID={organizationID}
          csvOptions={asyncOptions}
        />
      )}
    </>
  )
}

interface Input {
  name: string
  value: string
}

function filtersToAsyncOptions(
  filters: Record<string, string[]>,
  isAppsGrid: boolean
): object {
  return Object.keys(filters).reduce((acc, key) => {
    acc[filterKeyToInputName(key, isAppsGrid)] =
      key === 'applications' && filters[key] && filters[key][0]
        ? getApplicationFilter(key, isAppsGrid, filters)
        : filters[key]
    return acc
  }, {})
}

function getApplicationFilter(
  key: string,
  isAppsGrid: boolean,
  filters: Record<string, string[]>
) {
  const filterName = filterKeyToInputName(key, isAppsGrid)
  return filterName === 'applicationID' ? filters[key][0] : filters[key]
}

function filterKeyToInputName(name: string, isAppsGrid: boolean): string {
  switch (name) {
    case 'modules':
      return 'applicationIds'
    case 'statuses':
      return 'status'
    case 'types':
      return 'vulnTypes'
    case 'buildNumbers':
      return 'appVersionTags'
    case 'applications':
      return isAppsGrid ? 'applicationID' : 'applicationIds'
    default:
      return name
  }
}
